import type {
  AdminUsageDashboardResponse,
  UsageByKeyResponse,
  UsageDailyResponse,
  UsageHourlyResponse,
  UsageHourlyUnitsResponse,
  UsageRequestLogEntry,
  UsageRequestLogsResponse,
  UsageSummaryResponse,
  UsageUnitTotalsResponse,
} from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  type AdminUsageDashboardClientQuery,
  browserTimeZone,
  getAdminUsageDashboard,
  getUsageByKey,
  getUsageDaily,
  getUsageHourly,
  getUsageHourlyUnits,
  getUsageRequests,
  getUsageSummary,
  getUsageUnitTotals,
  type UsageDailyQuery,
  type UsageRange,
  type UsageRequestsQuery,
} from './usage.ts';

/**
 * React Query wrappers for tenant usage queries.
 *
 * Query key conventions:
 *   - `['usage', 'summary', from|'', to|'']`      — summary (range-keyed)
 *   - `['usage', 'hourly', days]`                 — hourly bucket series
 *   - `['usage', 'hourly-units', days]`           — per-hour unit quantities
 *   - `['usage', 'daily', days, from|'', to|'', tz]` — daily bucket series
 *   - `['usage', 'by-key', from|'', to|'']`       — per-key breakdown
 *   - `['usage', 'requests', …, detail|'']`       — request list (`detail` must
 *     be in the key so summary vs full do not share a cache entry)
 *   - `['usage', 'request-detail', requestId]`    — one archived row hydrate
 *   - `['usage', 'admin-dashboard', from, to, granularity, groupBy, model,
 *     vendor, apiKeyId, memberAccountId, department, q, limit, offset, tz]`
 *   - `['usage', 'unit-totals', …]`               — same dimensions, same
 *     order, as `admin-dashboard` (the two endpoints share one query string)
 *
 * Ranges are optional; an `undefined` range collapses to empty strings
 * in the key so "no filter" stays deterministic across renders. No
 * mutations; usage data is read-only.
 */

function rangeKey(range: UsageRange | undefined): readonly [string, string] {
  return [range?.from ?? '', range?.to ?? ''] as const;
}

export function useUsageSummary(range?: UsageRange): UseQueryResult<UsageSummaryResponse> {
  const { fetchImpl } = useSession();
  const [from, to] = rangeKey(range);
  return useQuery({
    queryKey: ['usage', 'summary', from, to] as const,
    queryFn: ({ signal }) => getUsageSummary(fetchImpl, range, signal),
  });
}

export function useUsageHourly(days: number): UseQueryResult<UsageHourlyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['usage', 'hourly', days] as const,
    queryFn: ({ signal }) => getUsageHourly(fetchImpl, days, signal),
  });
}

export function useUsageHourlyUnits(days: number): UseQueryResult<UsageHourlyUnitsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['usage', 'hourly-units', days] as const,
    queryFn: ({ signal }) => getUsageHourlyUnits(fetchImpl, days, signal),
  });
}

export function useUsageDaily(
  query: UsageDailyQuery,
  enabled = true,
): UseQueryResult<UsageDailyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    // #1678: the bucketing zone decides which calendar `days[].date` belongs
    // to, so it belongs in the key like any other query dimension. It reads
    // the SAME fallback the client serializer sends — an absent `tz` goes on
    // the wire as the browser's own zone, never as ''.
    queryKey: [
      'usage',
      'daily',
      query.days ?? 0,
      query.from ?? '',
      query.to ?? '',
      query.tz ?? browserTimeZone() ?? '',
    ] as const,
    queryFn: ({ signal }) => getUsageDaily(fetchImpl, query, signal),
    enabled,
  });
}

export function useUsageByKey(range?: UsageRange): UseQueryResult<UsageByKeyResponse> {
  const { fetchImpl } = useSession();
  const [from, to] = rangeKey(range);
  return useQuery({
    queryKey: ['usage', 'by-key', from, to] as const,
    queryFn: ({ signal }) => getUsageByKey(fetchImpl, range, signal),
  });
}

export function useUsageRequests(
  query?: UsageRequestsQuery,
): UseQueryResult<UsageRequestLogsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      'usage',
      'requests',
      query?.from ?? '',
      query?.to ?? '',
      query?.requestId ?? '',
      query?.apiKeyId ?? '',
      query?.status ?? '',
      query?.limit,
      query?.offset,
      query?.detail ?? '',
    ] as const,
    queryFn: ({ signal }) => getUsageRequests(fetchImpl, query, signal),
  });
}

/**
 * Single-row hydrate for an archived request. The list call uses
 * `detail=summary` and omits OSS blobs; expanding one archived row
 * re-fetches `/v1/usage/requests?requestId=` with no `detail` (server
 * default = hydrate) and no `from`/`to`.
 *
 * The key is deliberately not the list's `['usage','requests',…]` —
 * a summary page and a full single-row fetch must not share a cache
 * entry.
 */
export function useUsageRequestDetail(
  requestId: string | null,
  enabled: boolean,
): UseQueryResult<UsageRequestLogEntry | null> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['usage', 'request-detail', requestId] as const,
    queryFn: async ({ signal }): Promise<UsageRequestLogEntry | null> => {
      if (requestId === null || requestId.length === 0) return null;
      const page = await getUsageRequests(fetchImpl, { requestId }, signal);
      return page.rows[0] ?? null;
    },
    enabled: enabled && requestId !== null && requestId.length > 0,
  });
}

export function useAdminUsageDashboard(
  query?: AdminUsageDashboardClientQuery,
  enabled = true,
): UseQueryResult<AdminUsageDashboardResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      'usage',
      'admin-dashboard',
      query?.from ?? '',
      query?.to ?? '',
      query?.granularity ?? '',
      query?.groupBy ?? '',
      query?.model ?? '',
      // #1672: the model dimension is `vendor` + `model` together. Keying
      // on `model` alone makes two vendors of one model name share a
      // cache entry, so switching vendor would serve the other one's
      // numbers under the new label.
      query?.vendor ?? '',
      query?.apiKeyId ?? '',
      query?.memberAccountId ?? '',
      query?.department ?? '',
      query?.q ?? '',
      query?.limit ?? '',
      query?.offset ?? '',
      // #1678: the bucketing zone changes the bucket labels, so it belongs in
      // the cache key like any other query dimension.
      query?.tz ?? '',
    ] as const,
    queryFn: ({ signal }) => getAdminUsageDashboard(fetchImpl, query, signal),
    enabled,
  });
}

/**
 * Unit quantities for the admin usage page. The cache key lists exactly
 * the fields the request sends (`USAGE_UNIT_TOTALS_QUERY_FIELDS`, in that
 * order): a key that dropped one would serve one window's seconds under
 * another's label, and a key that kept granularity, groupBy, the seat
 * search or seat paging would re-run a whole-window unit read on every
 * seat page for an identical answer (#3185).
 *
 * `enabled` matches the dashboard query on purpose: the page fires them
 * together, and a paused dashboard must not leave this one running.
 */
export function useUsageUnitTotals(
  query?: AdminUsageDashboardClientQuery,
  enabled = true,
): UseQueryResult<UsageUnitTotalsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      'usage',
      'unit-totals',
      query?.from ?? '',
      query?.to ?? '',
      query?.model ?? '',
      query?.vendor ?? '',
      query?.apiKeyId ?? '',
      query?.memberAccountId ?? '',
      query?.department ?? '',
      query?.tz ?? '',
    ] as const,
    queryFn: ({ signal }) => getUsageUnitTotals(fetchImpl, query, signal),
    enabled,
  });
}
